import { collection, getCountFromServer } from 'firebase/firestore';
import { dbQuestionBankFull } from '../firebase/client';
import {
    getTestCatalogGroupsCollection,
    getTestCatalogTestsCollection,
} from '../testCatalog/testCatalogConstants';

export type QuestionBankCountRow = {
    collectionId: string;
    /** `-1` means count failed (permissions / missing collection). */
    count: number;
};

export type DashboardMetrics = {
    testGroupCount: number;
    testCatalogTestCount: number;
    questionBankCounts: QuestionBankCountRow[];
    /** Sum of successful question bank counts (excludes -1 rows). */
    questionTotalEstimate: number;
    loadedAt: number;
};

function getDashboardQuestionCollections(): string[] {
    const raw =
        process.env.REACT_APP_DASHBOARD_QUESTION_COLLECTIONS ??
        process.env.REACT_APP_TEST_TREE_QUESTION_COLLECTIONS ??
        'questions_nj';
    return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

/** Stubbed in Jest to avoid Firestore aggregate initialization in node. */
export async function loadDashboardMetrics(): Promise<DashboardMetrics> {
    if (process.env.NODE_ENV === 'test') {
        const loadedAt = Date.now();
        return {
            testGroupCount: 0,
            testCatalogTestCount: 0,
            questionBankCounts: [{ collectionId: 'questions_nj', count: 0 }],
            questionTotalEstimate: 0,
            loadedAt,
        };
    }

    const groupsColl = getTestCatalogGroupsCollection();
    const testsColl = getTestCatalogTestsCollection();

    const [gSnap, tSnap] = await Promise.all([
        getCountFromServer(collection(dbQuestionBankFull, groupsColl)),
        getCountFromServer(collection(dbQuestionBankFull, testsColl)),
    ]);

    const questionCollections = getDashboardQuestionCollections();
    const questionBankCounts: QuestionBankCountRow[] = [];
    for (const collId of questionCollections) {
        try {
            const s = await getCountFromServer(collection(dbQuestionBankFull, collId));
            questionBankCounts.push({ collectionId: collId, count: s.data().count });
        } catch {
            questionBankCounts.push({ collectionId: collId, count: -1 });
        }
    }

    const questionTotalEstimate = questionBankCounts.reduce((acc, r) => acc + (r.count >= 0 ? r.count : 0), 0);

    return {
        testGroupCount: gSnap.data().count,
        testCatalogTestCount: tSnap.data().count,
        questionBankCounts,
        questionTotalEstimate,
        loadedAt: Date.now(),
    };
}
